// MO4.1M
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import Flex from '@wix/da-ds/pkg/Flex';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { hashString } from '../../../utils/hash';
import MediaService, {
  fallbackToLargestFile,
} from '../../../utils/DeviationMediaService';
import TierStatisticsBar from '../../Helper/StatisticsBar/TierStatisticsBar';
import PriceWithDiscount from '../../../PriceWithDiscount';
import DiscountsPopupTrigger from '../../../DiscountsPopupTrigger';
import Benefits from '../Benefits';
import { formatPrice } from '../utils';
import { Props } from '../TierProductCardDumb';

import s from './TierProductCardDumbMobile.scss';

const THUMB_WIDTH = 360;
const THUMB_HEIGHT = 140;

const TierProductCardDumbMobile: React.FC<Props> = ({
  coverImage,
  title,
  tierState,
  stats,
  ctaButton,
  secondaryCtaButton,
  description,
  price = '',
  discount,
  allDiscounts = [],
  benefits = [],
  hasDiscord,
  info,
  withExpandButton,
  coverAreaExtrasLeft,
  coverAreaExtrasRight,
  isHighlighted,
  className,
  onClick,
  onToggleCollapsed,
  isCollapsed: isCollapsedProp,
  innerRef,
}) => {
  const { t } = useTranslation();
  const [isCollapsedState, setIsCollapsed] = useState(false);
  const isControlled = isCollapsedProp !== undefined;
  const isCollapsed = isControlled ? isCollapsedProp : isCollapsedState;
  const hasBenefits = benefits.length > 0 || hasDiscord;
  const isCollapsible = withExpandButton && hasBenefits;
  const benefitsHash = hashString(`benefits.${benefits.join('|')}`);

  const toggleCollapsed = useCallback(() => {
    if (isControlled) {
      onToggleCollapsed && onToggleCollapsed(!isCollapsed);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  }, [onToggleCollapsed, isControlled, isCollapsed]);

  const preview =
    coverImage &&
    MediaService.getSmallestThumbThatFullyCovers(
      coverImage,
      THUMB_WIDTH,
      THUMB_HEIGHT,
      fallbackToLargestFile,
      {
        allowPreview: true,
        allowFullview: false,
      }
    );

  return (
    <Flex
      className={classnames(
        s['root'],
        isHighlighted && s['root-highlight'],
        onClick && s['clickable-root'],
        className
      )}
      direction="column"
      onClick={onClick}
      innerRef={innerRef}
    >
      <div className={s['header']}>
        <div className={s['cover-wrapper']}>
          {coverAreaExtrasLeft && (
            <div className={s['cover-area-extras-left']}>
              {coverAreaExtrasLeft}
            </div>
          )}
          {coverAreaExtrasRight && (
            <div className={s['cover-area-extras-right']}>
              {coverAreaExtrasRight}
            </div>
          )}
          {preview ? (
            <div
              className={s['cover']}
              style={{
                width: '100%',
                height: THUMB_HEIGHT,
                backgroundImage: `url('${preview.src}')`,
                backgroundRepeat: 'no-repeat',
                backgroundPositionX: 'center',
                backgroundPositionY: 'center',
                backgroundSize: 'cover',
              }}
            />
          ) : (
            <div
              className={classnames(s['cover'], s['placeholder-cover'])}
              style={{ width: '100%', height: THUMB_HEIGHT }}
            />
          )}
          <div className={s['cover-overlay']}>
            {title && <Heading className={s['title']}>{title}</Heading>}
            <TierStatisticsBar className={s['stats']} stats={stats} />
          </div>
        </div>
      </div>

      <div className={s['card-body']}>
        {price && (
          <div className={s['price-block']}>
            {discount && (
              <PriceWithDiscount
                dollarAmount={price}
                eligibleDiscount={discount}
                textVariant="subscription"
                infix={
                  <span className={s['price-period']}>
                    /{t('deviationview.tierCard.pricePeriod.month')}
                  </span>
                }
              />
            )}
            {!discount && (
              <>
                <span className={s['price']}>{formatPrice(price)}</span>
                <span className={s['price-period']}>
                  /{t('deviationview.tierCard.pricePeriod.month')}
                </span>
              </>
            )}
            {allDiscounts.length > 0 && (
              <DiscountsPopupTrigger
                className={s['toggle-discounts-popup']}
                allDiscounts={allDiscounts}
                eligibleDiscount={discount}
              />
            )}
          </div>
        )}

        {ctaButton && (
          <div className={s['cta-buttons']}>
            {ctaButton}
            {secondaryCtaButton}
          </div>
        )}

        <div
          className={classnames(
            s['collapsible'],
            isCollapsed && s['collapsed']
          )}
        >
          {description && (
            <div
              className={classnames(
                s['description'],
                !hasBenefits && s['no-benefits']
              )}
            >
              {description}
            </div>
          )}

          {hasBenefits && (
            <Benefits
              key={benefitsHash}
              benefits={benefits}
              hasDiscord={hasDiscord}
            />
          )}
        </div>
      </div>

      <div className={s['card-footer']}>
        {info && (
          <div
            className={classnames(
              s['collapsible'],
              isCollapsed && s['collapsed']
            )}
          >
            <hr className={s['divider']} />
            <Flex className={s['info-block']} alignItems="flex-start">
              <InfoIcon
                size={IconSize.SMALLER}
                className={s['info-icon']}
                disableDefaultColors
              />
              <div className={s['info']}>{info}</div>
            </Flex>
          </div>
        )}

        {isCollapsible && (
          <>
            <hr className={s['divider']} />
            <PlainButtonOrLink
              onClick={toggleCollapsed}
              className={s['toggle-button']}
            >
              <span className={s['toggle-button-label']}>
                {isCollapsed
                  ? t('deviationview.tierCard.showMore')
                  : t('deviationview.tierCard.showLess')}
              </span>
              {isCollapsed ? (
                <ArrowDownIcon size={IconSize.SMALLER} disableDefaultColors />
              ) : (
                <ArrowUpIcon size={IconSize.SMALLER} disableDefaultColors />
              )}
            </PlainButtonOrLink>
          </>
        )}
      </div>
    </Flex>
  );
};
TierProductCardDumbMobile.displayName = 'TierProductCardDumbMobile';

export default TierProductCardDumbMobile;
